import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { HrConfigurationService } from 'humanresources-common-shared';
import { Observable } from 'rxjs';
import { PerEmployeeSubtypeQueryModel } from '../model/per-employee-subtype-query-model';
import { HvlPage, HvlResponse } from '@hvlng/framework-bff/core';
import { PerEmployeeSubtypeModel } from '../model/per-employee-subtype-model';
import { map } from 'rxjs/operators';
import { JsonConvert } from 'json2typescript';
import { LookupModel } from 'infrastructure-shared';

@Injectable({
    providedIn: 'root',
})
export class PerEmployeeSubtypeService {
    endpoint: string;

    constructor(private httpClient: HttpClient, private configService: HrConfigurationService) {
        this.endpoint = this.configService.config.hr.general + '/employeeSubtypeService/employeeSubtype/';
    }

    public delete(id: number): Observable<any> {
        return this.httpClient.delete(this.endpoint + id);
    }

    public queryPage(pagination: PerEmployeeSubtypeQueryModel): Observable<HvlPage<Array<PerEmployeeSubtypeModel>>> {
        return this.httpClient
            .post<HvlResponse<HvlPage<PerEmployeeSubtypeModel[]>>>(this.endpoint + 'page', pagination)
            .pipe(
                map((hvlPage) => {
                    let model = [];
                    if (hvlPage.body?.totalElements !== 0) {
                        const converter = new JsonConvert();
                        model = converter.deserializeArray(hvlPage.body.data, PerEmployeeSubtypeModel);
                    }
                    return new HvlPage<Array<PerEmployeeSubtypeModel>>(
                        model,
                        hvlPage.body.totalElements,
                        hvlPage.body.totalPages,
                        hvlPage.errorDetail,
                    );
                }),
            );
    }

    public update(model: PerEmployeeSubtypeModel): Observable<PerEmployeeSubtypeModel> {
        const converter = new JsonConvert();
        return this.httpClient
            .put<HvlResponse<PerEmployeeSubtypeModel>>(
                this.endpoint,
                converter.serialize(model, PerEmployeeSubtypeModel),
            )
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeObject(data.body, PerEmployeeSubtypeModel);
                }),
            );
    }

    public getById(id: number): Observable<PerEmployeeSubtypeModel> {
        return this.httpClient.get<HvlResponse<PerEmployeeSubtypeModel>>(this.endpoint + id).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeObject(data.body, PerEmployeeSubtypeModel);
            }),
        );
    }

    public create(model: PerEmployeeSubtypeModel): Observable<PerEmployeeSubtypeModel> {
        const converter = new JsonConvert();
        return this.httpClient
            .post<HvlResponse<PerEmployeeSubtypeModel>>(
                this.endpoint,
                converter.serialize(model, PerEmployeeSubtypeModel),
            )
            .pipe(
                map((data) => {
                    return converter.deserializeObject(data.body, PerEmployeeSubtypeModel);
                }),
            );
    }

    public queryList(queryModel: PerEmployeeSubtypeQueryModel): Observable<Array<PerEmployeeSubtypeModel>> {
        return this.httpClient
            .post<HvlResponse<Array<PerEmployeeSubtypeModel>>>(this.endpoint + 'query', queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerEmployeeSubtypeModel);
                }),
            );
    }

    public queryListByLocale(
        queryModel: PerEmployeeSubtypeQueryModel,
        locale: string,
    ): Observable<Array<PerEmployeeSubtypeModel>> {
        return this.httpClient
            .post<HvlResponse<Array<PerEmployeeSubtypeModel>>>(this.endpoint + 'query/byLocale/' + locale, queryModel)
            .pipe(
                map((data) => {
                    const converter = new JsonConvert();
                    return converter.deserializeArray(data.body || [], PerEmployeeSubtypeModel);
                }),
            );
    }

    public lookupQuery(queryModel: PerEmployeeSubtypeQueryModel): Observable<Array<LookupModel>> {
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(this.endpoint + 'lookup/query', queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }
    public lookupQueryWithEmployeeType(queryModel: PerEmployeeSubtypeQueryModel): Observable<Array<LookupModel>> {
        return this.httpClient.post<HvlResponse<Array<LookupModel>>>(this.endpoint + 'lookupQueryWithEmployeeType', queryModel).pipe(
            map((data) => {
                const converter = new JsonConvert();
                return converter.deserializeArray(data.body || [], LookupModel);
            }),
        );
    }
}
